'use client'

import { buildFilterUrl } from '@/app/[locale]/collection/_components/filter/element/filter-url-builder'
import { useCollectionFilterStoreV2 } from '@/app/[locale]/collection/store/collection-filter-store-v2'
import { shouldAddNofollowToFilterLinks } from '@/app/[locale]/collection/utils/filter-utils'
import { sortSizesFromStrings } from '@/lib/helpers/sort-size.helper'
import { cn } from '@/lib/utils'
import { LocalStorage } from '@/lib/utils/local-storage'
import { scrollToProductSection } from '@/lib/utils/scroll-utils'
import { TCardFacet, TFacet } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

type Props = {
  facets: TFacet[]
  card_facets: TCardFacet[]
}

export const FilterSizeSection = ({ facets, card_facets }: Props) => {
  const t = useTranslations('collection_page.title')
  const { setFilter, filters, originalSlug } = useCollectionFilterStoreV2(
    useShallow((state) => ({
      setFilter: state.setFilter,
      filters: state.filters,
      originalSlug: state.originalSlug,
    }))
  )

  // Find size facet
  const sizeFacet = facets.find((facet) => facet.seo_alias === 'size')

  // Normalize size filter to array and lowercase
  const sizeValue = filters?.size
  const sizeArray = useMemo(() => {
    if (Array.isArray(sizeValue)) {
      return sizeValue
    } else if (sizeValue) {
      const sizeStr = String(sizeValue)
      // Handle comma-separated values (e.g., "3xl,xs,4xl")
      if (sizeStr.includes(',')) {
        return sizeStr.split(',').map((s) => s.trim())
      } else {
        return [sizeStr]
      }
    }
    return []
  }, [sizeValue])

  const normalizedSizeArray = useMemo(
    () =>
      sizeArray
        .map((s) => String(s).trim().toLowerCase())
        .filter((s) => s.length > 0),
    [sizeArray]
  )

  const sizeList = useMemo(
    () => sizeFacet?.facet_values?.map((v) => v.title) || [],
    [sizeFacet]
  )

  const sortedSizes = useMemo(() => sortSizesFromStrings(sizeList), [sizeList])

  if (
    !sizeFacet ||
    !sizeFacet.facet_values ||
    sizeFacet.facet_values.length === 0
  ) {
    return null
  }

  const handleSizeClick = (size: string) => {
    const sizeLower = size.toLowerCase()
    const newSizes = normalizedSizeArray.includes(sizeLower)
      ? normalizedSizeArray.filter((s) => s !== sizeLower)
      : [...normalizedSizeArray, sizeLower]

    if (newSizes.length === 0) {
      setFilter('size', undefined)
    } else {
      setFilter('size', newSizes)
    }

    LocalStorage.set('COLLECTION_FILTER_SIZE', newSizes)

    // Cuộn lên đầu danh sách sản phẩm sau khi thay đổi filter
    scrollToProductSection(200, 50)
  }

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    size: string
  ) => {
    e.preventDefault()
    handleSizeClick(size)
  }

  // Check if should add nofollow
  const shouldAddNofollow = shouldAddNofollowToFilterLinks(
    filters,
    facets,
    card_facets
  )

  // Build URL for each size option
  const getFilterUrl = (size: string): string => {
    if (!originalSlug) return '#'
    const sizeLower = size.toLowerCase()
    const isChecked = normalizedSizeArray.includes(sizeLower)
    return buildFilterUrl({
      originalSlug,
      currentFilters: filters,
      facetKey: 'size',
      facetValue: sizeLower,
      isChecked: !isChecked, // URL for toggled state
      isMultiple: true, // Size allows multiple values
      isConcatSlug: false, // Size doesn't use concat slug
      facets,
      cardFacets: card_facets,
    })
  }

  return (
    <div className='mb-6'>
      <h2 className='mb-3 text-xs text-neutral-500 max-md:hidden md:text-sm'>
        {t('filter_size')}
      </h2>
      <div className='no-scrollbar flex gap-2 max-md:overflow-x-auto max-md:[width:calc(5.1*(64px+8px))] md:flex-wrap'>
        {sortedSizes.map((size) => {
          const filterUrl = getFilterUrl(size)
          const sizeLower = size.toLowerCase()
          const isActive = normalizedSizeArray.includes(sizeLower)

          return (
            <a
              key={size}
              href={filterUrl}
              onClick={(e) => handleLinkClick(e, size)}
              className={cn(
                'flex min-w-16 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-neutral-300 px-3 py-2 font-criteria text-sm uppercase leading-4.5 text-neutral-900 no-underline transition-colors ease-linear md:min-w-20 md:text-base',
                isActive
                  ? 'bg-neutral-900 text-white'
                  : 'bg-neutral-300 hover:bg-neutral-900 hover:text-white'
              )}
              aria-label={`Filter by size ${size}`}
              rel={shouldAddNofollow ? 'nofollow' : undefined}
              ga-tracking-value={`filter_size__${sizeLower}`}
              ga-tracking-label={`filter_size__${originalSlug}`}
            >
              {size}
            </a>
          )
        })}
      </div>
    </div>
  )
}
